import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertCircle, Check } from "lucide-react";
import { toast } from "sonner";
import * as adminApi from "@/api/admin";
import {
  LandingPageSubmitError,
  IntegrationRequestError,
  type LandingPageSection,
  type QuestionnaireField,
} from "@/api/admin";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface QuestionnaireFormProps {
  slug: string;
  section: LandingPageSection;
}

type AnswerValue = string | string[] | boolean;

const inputBase =
  "w-full rounded-xl bg-white/5 border px-4 py-2.5 text-sm text-white placeholder-white/30 " +
  "focus:outline-none focus:ring-2 focus:ring-[#00d9d9]/50 focus:border-[#00d9d9]/50 transition-colors";

// Dispatches to a campaign-specific flow when the section is wired to one,
// otherwise falls back to the generic field-by-field form. Add new
// campaigns here alongside their handler component.
export function QuestionnaireForm({ slug, section }: QuestionnaireFormProps) {
  if (section.integration_code === "unistudents") {
    return (
      <QivosCampaignForm
        slug={slug}
        section={section}
        countryOptions={[
          { value: "gr", label: "Ελλάδα (+30)" },
          { value: "cy", label: "Κύπρος (+357)" },
        ]}
      />
    );
  }
  if (section.integration_code === "dimos_athinaion") {
    return (
      <QivosCampaignForm
        slug={slug}
        section={section}
        countryOptions={[{ value: "gr", label: "Ελλάδα (+30)" }]}
      />
    );
  }
  if (section.integration_code) {
    // A code is set but we don't have a matching UI for it — surfacing
    // this rather than silently rendering the generic form, which would
    // collect answers that nothing ever acts on.
    return (
      <div className="flex items-center gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2.5">
        <AlertCircle size={14} className="shrink-0" />
        Άγνωστη ρύθμιση φόρμας ({section.integration_code}).
      </div>
    );
  }
  return <GenericQuestionnaireForm slug={slug} section={section} />;
}

// ---------------------------------------------------------------------------
// Shared field rendering — used by both the generic form and (for every
// field except Phone, which gets a country selector bolted on) the
// Unistudents form, so the two stay visually and behaviorally consistent.
// ---------------------------------------------------------------------------

function FieldInput({
  field,
  value,
  error,
  onChange,
  onToggleMultiSelect,
  extra,
}: {
  field: QuestionnaireField;
  value: AnswerValue;
  error?: string;
  onChange: (value: AnswerValue) => void;
  onToggleMultiSelect: (option: string) => void;
  /** Rendered to the right of a text-like input, e.g. the country selector for Phone. */
  extra?: React.ReactNode;
}) {
  const borderClass = error ? "border-red-500/50" : "border-white/15";

  if (field.field_type === "checkbox") {
    return (
      <div>
        <label className="flex items-start gap-2.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={value as boolean}
            onChange={(e) => onChange(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-white/30 bg-white/5 accent-[#00d9d9] shrink-0"
          />
          <span
            className="text-sm text-white/70 leading-snug [&_a]:text-[#00d9d9] [&_a]:underline [&_a]:underline-offset-2"
            dangerouslySetInnerHTML={{ __html: field.label }}
          />
        </label>
        {error && <p className="text-xs text-red-300 mt-1 ml-6">{error}</p>}
      </div>
    );
  }

  if (field.field_type === "multiple_choice") {
    return (
      <div>
        <label className="block text-sm font-medium text-white mb-1.5">
          {field.label} {field.required && <span className="text-[#00d9d9]">*</span>}
        </label>
        <Select value={(value as string) || undefined} onValueChange={onChange}>
          <SelectTrigger
            className={`w-full rounded-xl bg-white/5 border px-4 py-2.5 h-auto text-sm text-white ${borderClass} focus:ring-2 focus:ring-[#00d9d9]/50 focus:border-[#00d9d9]/50 data-[placeholder]:text-white/30`}
          >
            <SelectValue placeholder="Επίλεξε…" />
          </SelectTrigger>
          <SelectContent className="bg-[#1a1a1a] border-white/15 text-white">
            {field.options.map((opt) => (
              <SelectItem key={opt} value={opt} className="text-white focus:bg-white/10 focus:text-white">
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {error && <p className="text-xs text-red-300 mt-1.5">{error}</p>}
      </div>
    );
  }

  if (field.field_type === "multiple_select") {
    const selected = (value as string[]) || [];
    return (
      <div>
        <p className="text-sm font-medium text-white mb-2.5">
          {field.label} {field.required && <span className="text-[#00d9d9]">*</span>}
        </p>
        <div className="space-y-2">
          {field.options.map((opt) => (
            <label key={opt} className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => onToggleMultiSelect(opt)}
                className="h-4 w-4 rounded accent-[#00d9d9]"
              />
              <span className="text-sm text-white/70">{opt}</span>
            </label>
          ))}
        </div>
        {error && <p className="text-xs text-red-300 mt-1.5">{error}</p>}
      </div>
    );
  }

  // first_name / last_name / email / phone
  return (
    <div>
      <label className="block text-sm font-medium text-white mb-1.5">
        {field.label} {field.required && <span className="text-[#00d9d9]">*</span>}
      </label>
      <div className={extra ? "grid grid-cols-[auto_1fr] gap-3" : undefined}>
        {extra}
        <input
          type={field.field_type === "email" ? "email" : field.field_type === "phone" ? "tel" : "text"}
          inputMode={field.field_type === "phone" ? "tel" : undefined}
          value={value as string}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputBase} ${borderClass}`}
          placeholder={field.field_type === "phone" ? "69XXXXXXXX" : undefined}
        />
      </div>
      {error && <p className="text-xs text-red-300 mt-1.5">{error}</p>}
    </div>
  );
}

function emptyValuesFor(fields: QuestionnaireField[]): Record<number, AnswerValue> {
  const initial: Record<number, AnswerValue> = {};
  fields.forEach((f) => {
    if (f.field_type === "checkbox") initial[f.id!] = false;
    else if (f.field_type === "multiple_select") initial[f.id!] = [];
    else initial[f.id!] = "";
  });
  return initial;
}

// ---------------------------------------------------------------------------
// Unistudents collab: renders every field on the section (same widgets as
// the generic form), plus a country selector next to the Phone field.
// All required fields — including consent — must pass client-side
// validation before start() is even called. On submit: the full answer set
// is always stored as a normal LandingPageSubmission, and the Phone field's
// value drives the Qivos flow (Search -> Journey A/B -> possibly an OTP step).
// ---------------------------------------------------------------------------

interface QivosCampaignFormProps extends QuestionnaireFormProps {
  countryOptions: { value: string; label: string }[];
}

function QivosCampaignForm({ slug, section, countryOptions }: QivosCampaignFormProps) {
  const navigate = useNavigate();
  const fields = section.questionnaire_fields;
  const phoneField = fields.find((f) => f.field_type === "phone");

  const [values, setValues] = useState<Record<number, AnswerValue>>(() => emptyValuesFor(fields));
  const [country, setCountry] = useState(countryOptions[0]?.value ?? "gr");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState<"form" | "otp">("form");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [otpNotice, setOtpNotice] = useState<string | null>(null);

  function setValue(fieldId: number, value: AnswerValue) {
    setValues((prev) => ({ ...prev, [fieldId]: value }));
    setFieldErrors((prev) => {
      if (!prev[fieldId]) return prev;
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  }

  function toggleMultiSelect(fieldId: number, option: string) {
    setValues((prev) => {
      const current = (prev[fieldId] as string[]) || [];
      const next = current.includes(option)
        ? current.filter((o) => o !== option)
        : [...current, option];
      return { ...prev, [fieldId]: next };
    });
  }

  /**
   * Mirrors the server's required/checkbox rules so we can block the OTP
   * flow from ever starting on incomplete/unconsented submissions, rather
   * than only finding out after a round trip. The server re-validates
   * everything regardless — this is a UX gate, not the source of truth.
   */
  function validateClientSide(): boolean {
    const errors: Record<string, string> = {};
    fields.forEach((field) => {
      const value = values[field.id!];
      if (field.field_type === "checkbox") {
        if (field.required && value !== true) {
          errors[String(field.id)] = "You must accept this to continue.";
        }
        return;
      }
      if (field.required) {
        const isEmpty =
          value === "" || value === undefined || (Array.isArray(value) && value.length === 0);
        if (isEmpty) {
          errors[String(field.id)] = "This field is required.";
        }
      }
    });
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  function finishWithSuccess(points?: string) {
    toast.success("Ευχαριστούμε!", {
      description: points ? `Κέρδισες ${points} πόντους PADU. Μπες στο PADU app και ξεκλείδωσε μοναδικές προσφορές.` : "Η εγγραφή σου ολοκληρώθηκε.",
    });
    navigate("/");
  }

  function finishAlreadyClaimed() {
    toast("Έχεις ήδη συμμετάσχει", {
      description: "Αυτός ο αριθμός έχει ήδη λάβει τους πόντους.",
    });
    navigate("/");
  }

  async function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validateClientSide()) return;

    setSubmitting(true);
    setGeneralError(null);
    try {
      const answers: Record<string, AnswerValue> = {};
      Object.entries(values).forEach(([fieldId, value]) => {
        answers[fieldId] = value;
      });
      const result = await adminApi.startSectionIntegration(slug, section.id!, {
        answers,
        country,
      });
      switch (result.status) {
        case "success":
          finishWithSuccess(result.points);
          break;
        case "already_claimed":
          finishAlreadyClaimed();
          break;
        case "otp_required":
          setOtpNotice("Στείλαμε κωδικό επιβεβαίωσης στο κινητό σου.");
          setStep("otp");
          break;
        case "otp_max_attempts":
          setGeneralError("Έχεις φτάσει το μέγιστο όριο κωδικών. Δοκίμασε ξανά αργότερα.");
          break;
        default:
          setGeneralError("Κάτι πήγε στραβά. Δοκίμασε ξανά.");
      }
    } catch (err) {
      if (err instanceof LandingPageSubmitError) {
        setFieldErrors(err.errors);
      } else if (err instanceof IntegrationRequestError) {
        setGeneralError(err.detail);
      } else {
        setGeneralError("Κάτι πήγε στραβά. Δοκίμασε ξανά.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleOtpSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setGeneralError(null);
    try {
      const mobile = String(values[phoneField!.id!] || "").trim();
      const result = await adminApi.verifySectionIntegration(slug, section.id!, {
        mobile,
        country,
        otp: otp.trim(),
      });
      switch (result.status) {
        case "success":
          finishWithSuccess(result.points);
          break;
        case "already_claimed":
          finishAlreadyClaimed();
          break;
        case "invalid_otp":
          setGeneralError("Λάθος κωδικός. Δοκίμασε ξανά.");
          break;
        default:
          setGeneralError("Κάτι πήγε στραβά. Δοκίμασε ξανά.");
      }
    } catch (err) {
      setGeneralError(err instanceof IntegrationRequestError ? err.detail : "Κάτι πήγε στραβά. Δοκίμασε ξανά.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!phoneField) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2.5">
        <AlertCircle size={14} className="shrink-0" />
        Αυτή η φόρμα δεν έχει ρυθμιστεί σωστά (λείπει το πεδίο τηλεφώνου).
      </div>
    );
  }

  if (step === "otp") {
    return (
      <form onSubmit={handleOtpSubmit} className="space-y-5">
        {otpNotice && (
          <div className="flex items-center gap-2 text-sm text-[#00d9d9] bg-[#00d9d9]/10 border border-[#00d9d9]/30 rounded-xl px-3 py-2.5">
            <Check size={14} className="shrink-0" />
            {otpNotice}
          </div>
        )}
        {generalError && (
          <div className="flex items-center gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2.5">
            <AlertCircle size={14} className="shrink-0" />
            {generalError}
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-white mb-1.5">
            Κωδικός επιβεβαίωσης <span className="text-[#00d9d9]">*</span>
          </label>
          <input
            type="text"
            inputMode="numeric"
            autoFocus
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            className={`${inputBase} border-white/15`}
            placeholder="123456"
          />
        </div>
        <button
          type="submit"
          disabled={submitting || !otp.trim()}
          className="w-full bg-[#00d9d9] hover:scale-[1.02] text-black font-bold font-press-start text-xs sm:text-sm rounded-full py-4 transition-transform duration-200 disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-2"
        >
          {submitting ? <Loader2 size={16} className="animate-spin" /> : "Επιβεβαίωση"}
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={handleFormSubmit} className="space-y-5">
      {generalError && (
        <div className="flex items-center gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2.5">
          <AlertCircle size={14} className="shrink-0" />
          {generalError}
        </div>
      )}

      {fields.map((field) => {
        const isPhone = field.field_type === "phone";
        return (
          <FieldInput
            key={field.id}
            field={field}
            value={values[field.id!]}
            error={fieldErrors[String(field.id)]}
            onChange={(value) => setValue(field.id!, value)}
            onToggleMultiSelect={(opt) => toggleMultiSelect(field.id!, opt)}
            extra={
              isPhone && countryOptions.length > 1 ? (
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger className="w-28 rounded-xl bg-white/5 border border-white/15 px-3 py-2.5 h-auto text-sm text-white focus:ring-2 focus:ring-[#00d9d9]/50 focus:border-[#00d9d9]/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/15 text-white">
                    {countryOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} className="text-white focus:bg-white/10 focus:text-white">
                        {opt.value.toUpperCase()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : undefined
            }
          />
        );
      })}

      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-[#00d9d9] hover:scale-[1.02] text-black font-bold font-press-start text-xs sm:text-sm rounded-full py-4 transition-transform duration-200 disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-2"
      >
        {submitting ? <Loader2 size={16} className="animate-spin" /> : "Συνέχεια"}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Generic questionnaire: unchanged behavior, now built on the shared
// FieldInput so it stays in sync with the Unistudents form's rendering.
// ---------------------------------------------------------------------------

function GenericQuestionnaireForm({ slug, section }: QuestionnaireFormProps) {
  const navigate = useNavigate();
  const fields = section.questionnaire_fields;

  const [values, setValues] = useState<Record<number, AnswerValue>>(() => emptyValuesFor(fields));
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [generalError, setGeneralError] = useState(false);

  function setValue(fieldId: number, value: AnswerValue) {
    setValues((prev) => ({ ...prev, [fieldId]: value }));
    setFieldErrors((prev) => {
      if (!prev[fieldId]) return prev;
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  }

  function toggleMultiSelect(fieldId: number, option: string) {
    setValues((prev) => {
      const current = (prev[fieldId] as string[]) || [];
      const next = current.includes(option)
        ? current.filter((o) => o !== option)
        : [...current, option];
      return { ...prev, [fieldId]: next };
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setGeneralError(false);
    setFieldErrors({});
    try {
      const answers: Record<string, AnswerValue> = {};
      Object.entries(values).forEach(([fieldId, value]) => {
        answers[fieldId] = value;
      });
      await adminApi.submitLandingPageQuestionnaire(slug, section.id!, answers);
      toast.success("Ευχαριστούμε!", {
        description: "Η απάντησή σου καταχωρήθηκε.",
      });
      navigate("/");
    } catch (err) {
      if (err instanceof LandingPageSubmitError) {
        setFieldErrors(err.errors);
      } else {
        setGeneralError(true);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {generalError && (
        <div className="flex items-center gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2.5">
          <AlertCircle size={14} className="shrink-0" />
          Κάτι πήγε στραβά. Δοκίμασε ξανά.
        </div>
      )}

      {fields.map((field) => (
        <FieldInput
          key={field.id}
          field={field}
          value={values[field.id!]}
          error={fieldErrors[String(field.id)]}
          onChange={(value) => setValue(field.id!, value)}
          onToggleMultiSelect={(opt) => toggleMultiSelect(field.id!, opt)}
        />
      ))}

      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-[#00d9d9] hover:scale-[1.02] text-black font-bold font-press-start text-xs sm:text-sm rounded-full py-4 transition-transform duration-200 disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-2"
      >
        {submitting ? <Loader2 size={16} className="animate-spin" /> : "Υποβολή"}
      </button>
    </form>
  );
}
